/**
 * @fileoverview Modal "Novo card" do CRM — redesign premium.
 *
 * Stack reaproveitada do projeto:
 *  - react-quill (NewsPostModal já usa) → rich text de descrição
 *  - stringToColor/initials (UsersTable) → avatars no atribuído
 *  - tokens de modalDesign.js → consistência visual
 *
 * Compatibilidade:
 *  - Descrição agora salva HTML em `customFields.description`. LeadDetailModal
 *    já chama `sanitizeHtml` ao renderizar — texto plano antigo continua OK.
 *  - SLA fica em `customFields.slaDate` (ISO datetime). Zero migração de DB.
 *  - Todo o resto (anexos, custom fields, checklist, IA rephrase, capa) está
 *    preservado integralmente.
 */

import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Popover from "@material-ui/core/Popover";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";

import InputMask from "react-input-mask";
import NumberFormat from "react-number-format";
import ReactQuill from "react-quill";
import { ChecklistItemRow } from "../LeadShared";
import "react-quill/dist/quill.snow.css";

import {
  Sparkles,
  Eye,
  Pencil,
  Plus,
  Trash2,
  X,
  MoreVertical,
  Users,
  File as FileIcon,
  Image as ImageIcon,
  UploadCloud,
  FileText,
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  Search,
  Check,
  UserRound,
  // Field type icons (lucide — substituem @material-ui/icons)
  Type as TypeIcon,
  MessageCircle as WhatsappIconLc,
  IdCard as CpfIconLc,
  Mail as EmailIconLc,
  DollarSign as CurrencyIconLc,
  ListChecks as ChecklistIconLc,
  Users2 as UsersIconLc,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import AiReformulateDescriptionModal from "../AiReformulateDescriptionModal";
import usePermission from "../../hooks/usePermission";
import { stringToColor, initials } from "../UsersTable";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
// Quill considera "<p><br></p>" como vazio — checamos via textContent.
const quillIsEmpty = (html) => {
  if (!html) return true;
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  const text = (tmp.textContent || "").trim();
  return text.length === 0;
};

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// SLA — diff em DIAS (arredondado pra inteiro), feedback visual em escala
// de urgência. Datetime-local: aceita string "YYYY-MM-DDTHH:MM".
const computeSlaStatus = (isoDatetime) => {
  if (!isoDatetime) return null;
  const target = new Date(isoDatetime);
  if (Number.isNaN(target.getTime())) return null;

  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / 86400000);
  const diffHours = Math.round(diffMs / 3600000);

  // Vencido
  if (diffMs < 0) {
    const absDays = Math.abs(diffDays);
    return {
      tone: "overdue",
      color: "#ef4444",
      bg: "rgba(239,68,68,0.12)",
      border: "rgba(239,68,68,0.30)",
      icon: AlertCircle,
      label:
        absDays === 0
          ? `Vencido há ${Math.abs(diffHours)}h`
          : absDays === 1
            ? "Vencido há 1 dia"
            : `Vencido há ${absDays} dias`,
    };
  }
  // Vence em <= 24h
  if (diffMs < 24 * 3600 * 1000) {
    return {
      tone: "today",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.14)",
      border: "rgba(245,158,11,0.32)",
      icon: Clock,
      label:
        diffHours <= 1
          ? "Vence em menos de 1h"
          : diffHours < 6
            ? `Vence em ${diffHours}h`
            : "Vence hoje",
    };
  }
  // 1-3 dias
  if (diffDays <= 3) {
    return {
      tone: "soon",
      color: "#f59e0b",
      bg: "rgba(245,158,11,0.10)",
      border: "rgba(245,158,11,0.26)",
      icon: Clock,
      label: diffDays === 1 ? "Vence amanhã" : `Vence em ${diffDays} dias`,
    };
  }
  return {
    tone: "ok",
    color: "#22c55e",
    bg: "rgba(34,197,94,0.10)",
    border: "rgba(34,197,94,0.24)",
    icon: CheckCircle2,
    label: `${diffDays} dias até o prazo`,
  };
};

// Datetime-local helpers: input "type=datetime-local" usa formato
// `YYYY-MM-DDTHH:MM` em horário local (sem timezone).
const pad = (n) => String(n).padStart(2, "0");
const dateToLocalInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;

const slaPreset = (days, hour = 18, minute = 0) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return dateToLocalInput(d);
};

const formatSlaShort = (isoDatetime) => {
  if (!isoDatetime) return "";
  const d = new Date(isoDatetime);
  if (Number.isNaN(d.getTime())) return "";
  const dateStr = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
  const timeStr = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  return `${dateStr} às ${timeStr}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Estilos
// ─────────────────────────────────────────────────────────────────────────────
const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    // ── Dialog shell ──
    dialog: {
      "& .MuiDialog-paper": {
        borderRadius: 22,
        background: dark ? "#0E0F12" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        boxShadow: dark
          ? "0 32px 90px -16px rgba(0,0,0,0.7), 0 12px 32px -8px rgba(0,0,0,0.5)"
          : "0 32px 90px -16px rgba(15,23,42,0.20), 0 12px 32px -8px rgba(15,23,42,0.12)",
        maxWidth: 780,
        width: "100%",
        margin: 16,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        maxHeight: "92vh",
      },
    },

    // ── HEADER ──
    // ── HEADER MICRO (estilo Jira/Linear) ──
    // Não tem ícone gigante nem subtítulo. Só uma label tiny "Novo card"
    // como breadcrumb + close. O título do card é o protagonista, logo abaixo.
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "12px 16px 8px",
      flexShrink: 0,
    },
    headerCrumb: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "3px 9px",
      borderRadius: 999,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      border: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.05)",
    },
    headerCrumbDot: {
      width: 6, height: 6, borderRadius: "50%",
      background: dark ? "#7C9BFF" : "#3D5CFD",
      flexShrink: 0,
    },
    closeBtn: {
      width: 32,
      height: 32,
      border: "none",
      background: "transparent",
      borderRadius: 8,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      transition: "background .15s, color .15s",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },

    // ── BODY ──
    body: {
      padding: "4px 24px 14px",
      overflowY: "auto",
      flex: 1,
      minHeight: 0,
      ...theme.scrollbarStylesSoft,
    },
    section: { marginBottom: 22, "&:last-child": { marginBottom: 6 } },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 10,
      gap: 8,
    },
    sectionLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#71717A",
    },

    // ── Inputs ──
    fieldInput: {
      "& .MuiOutlinedInput-root": {
        borderRadius: 11,
        background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
        transition: "border-color .15s, box-shadow .15s, background .15s",
        fontSize: 14,
        "& fieldset": {
          borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)",
          transition: "border-color .15s",
        },
        "&:hover fieldset": {
          borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.30)",
        },
        "&.Mui-focused": {
          background: dark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
        },
        "&.Mui-focused fieldset": {
          borderColor: dark ? "#5B7CFF" : "#3D5CFD",
          borderWidth: 1,
          boxShadow: dark
            ? "0 0 0 1px rgba(91,124,255,0.14)"
            : "0 0 0 1px rgba(61,92,253,0.10)",
        },
        "&.Mui-error fieldset": { borderColor: "#ef4444" },
        "&.Mui-error.Mui-focused fieldset": {
          borderColor: "#ef4444",
          boxShadow: "0 0 0 1px rgba(239,68,68,0.16)",
        },
      },
      "& .MuiOutlinedInput-input": { padding: "12px 14px" },
      "& .MuiOutlinedInput-multiline": { padding: "12px 14px" },
    },
    titleField: {
      "& .MuiOutlinedInput-input": { fontSize: 15.5, fontWeight: 500, padding: "13px 16px" },
    },
    // Título HERO — sem borda visível, fonte grande, vira o foco visual do
    // modal. Inspirado em Linear/Jira issue composer.
    titleHero: {
      "& .MuiOutlinedInput-root": {
        background: "transparent",
        padding: 0,
        "& fieldset": { border: "none" },
        "&:hover fieldset": { border: "none" },
        "&.Mui-focused fieldset": { border: "none", boxShadow: "none" },
        "&.Mui-focused": { background: "transparent" },
      },
      "& .MuiOutlinedInput-input": {
        fontSize: 22,
        fontWeight: 600,
        letterSpacing: "-0.4px",
        lineHeight: 1.3,
        padding: "4px 24px 8px",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
      "& .MuiOutlinedInput-input::placeholder": {
        color: dark ? "rgba(255,255,255,0.28)" : "rgba(15,23,42,0.32)",
        opacity: 1,
        fontWeight: 500,
      },
    },
    titleHeroError: {
      // estado de erro: linha vermelha sutil no fundo da linha (sem borda
      // pesada) + ícone de aviso à direita
      "& .MuiOutlinedInput-input": {
        boxShadow: `inset 0 -1.5px 0 #ef4444`,
      },
    },
    fieldError: {
      fontSize: 11.5,
      color: "#ef4444",
      marginTop: 5,
      display: "flex",
      alignItems: "center",
      gap: 5,
    },

    // ── Quill editor ──
    quillWrap: {
      borderRadius: 11,
      overflow: "hidden",
      background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
      border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)"}`,
      transition: "border-color .15s, box-shadow .15s",
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.30)" : "rgba(61,92,253,0.28)",
      },
      "&:focus-within": {
        borderColor: dark ? "#5B7CFF" : "#3D5CFD",
        boxShadow: dark
          ? "0 0 0 1px rgba(91,124,255,0.14)"
          : "0 0 0 1px rgba(61,92,253,0.10)",
      },
      // Customização da Quill em si: remover borda padrão, ajustar toolbar
      "& .ql-toolbar.ql-snow": {
        borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
        borderTop: "none",
        borderLeft: "none",
        borderRight: "none",
        padding: "8px 10px",
        background: dark ? "rgba(255,255,255,0.015)" : "rgba(15,23,42,0.02)",
      },
      "& .ql-container.ql-snow": {
        border: "none",
        fontSize: 14,
        fontFamily: "inherit",
      },
      "& .ql-editor": {
        minHeight: 120,
        maxHeight: 280,
        color: dark ? "#FAFAFA" : "#0F172A",
        padding: "12px 14px",
        lineHeight: 1.55,
      },
      "& .ql-editor.ql-blank::before": {
        color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
        fontStyle: "normal",
        left: 14,
        right: 14,
      },
      // Botões da toolbar (cores em dark)
      "& .ql-snow .ql-stroke": {
        stroke: dark ? "rgba(255,255,255,0.65)" : "#475569",
      },
      "& .ql-snow .ql-fill": {
        fill: dark ? "rgba(255,255,255,0.65)" : "#475569",
      },
      "& .ql-snow .ql-picker": {
        color: dark ? "rgba(255,255,255,0.65)" : "#475569",
      },
      "& .ql-snow button:hover .ql-stroke, & .ql-snow .ql-picker-label:hover .ql-stroke": {
        stroke: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow button:hover .ql-fill, & .ql-snow .ql-picker-label:hover .ql-fill": {
        fill: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow button.ql-active .ql-stroke, & .ql-snow .ql-picker-label.ql-active .ql-stroke": {
        stroke: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow button.ql-active .ql-fill, & .ql-snow .ql-picker-label.ql-active .ql-fill": {
        fill: dark ? "#A5B4FC" : "#3D5CFD",
      },
      "& .ql-snow .ql-picker-options": {
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.08)",
        borderRadius: 8,
      },
      // Code block
      "& .ql-editor pre.ql-syntax, & .ql-editor pre": {
        background: dark ? "rgba(0,0,0,0.35)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
        borderRadius: 8,
        padding: "10px 12px",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
        fontSize: 13,
      },
      "& .ql-editor blockquote": {
        borderLeft: `3px solid ${dark ? "#5B7CFF" : "#3D5CFD"}`,
        paddingLeft: 12,
        color: dark ? "rgba(255,255,255,0.75)" : "#475569",
        marginLeft: 0,
        marginRight: 0,
      },
    },

    // ── Detalhes (grid 3) ──
    detailsGrid: {
      display: "grid",
      gridTemplateColumns: "1.1fr 1fr 1fr",
      gap: 12,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    detailCell: { display: "flex", flexDirection: "column", gap: 7, minWidth: 0 },
    detailCellLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      color: dark ? "rgba(255,255,255,0.45)" : "#71717A",
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    },

    // ── Prioridade segmented ──
    prioritySegment: {
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 4,
      padding: 4,
      borderRadius: 11,
      background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.04)",
      border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    priorityPill: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      height: 30,
      borderRadius: 8,
      border: "none",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      fontSize: 11,
      fontWeight: 600,
      cursor: "pointer",
      transition: "background .12s, color .12s",
      fontFamily: "inherit",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    priorityPillActive: {
      background: dark ? "rgba(255,255,255,0.07)" : "#FFFFFF",
      color: dark ? "#FAFAFA" : "#0F172A",
      boxShadow: dark
        ? "0 1px 2px rgba(0,0,0,0.30), 0 0 0 1px rgba(255,255,255,0.04)"
        : "0 1px 2px rgba(15,23,42,0.06), 0 0 0 1px rgba(15,23,42,0.04)",
    },
    priorityDot: { width: 6, height: 6, borderRadius: "50%", flexShrink: 0 },

    // ── Assignee picker ──
    assigneeTrigger: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      height: 40,
      padding: "0 12px",
      borderRadius: 11,
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
      background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
      cursor: "pointer",
      transition: "border-color .15s, background .15s",
      width: "100%",
      fontFamily: "inherit",
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.28)",
        background: dark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
      },
    },
    assigneeAvatar: {
      width: 26,
      height: 26,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      flexShrink: 0,
      overflow: "hidden",
      position: "relative",
    },
    assigneeAvatarImg: {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
    },
    assigneeAvatarPlaceholder: {
      width: 26,
      height: 26,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      color: dark ? "rgba(255,255,255,0.4)" : "#94A3B8",
    },
    assigneeName: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      textAlign: "left",
    },
    assigneeNamePlaceholder: { color: dark ? "rgba(255,255,255,0.4)" : "#94A3B8" },
    assigneeChevron: {
      color: dark ? "rgba(255,255,255,0.4)" : "#94A3B8",
      flexShrink: 0,
    },
    // Popover
    pickerPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 14,
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        boxShadow: dark
          ? "0 20px 60px -12px rgba(0,0,0,0.6), 0 8px 24px -6px rgba(0,0,0,0.4)"
          : "0 20px 60px -12px rgba(15,23,42,0.18), 0 8px 24px -6px rgba(15,23,42,0.10)",
        width: 320,
        maxHeight: 380,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      },
    },
    pickerSearchWrap: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 12px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    pickerSearchIcon: { color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8", flexShrink: 0 },
    pickerSearchInput: {
      flex: 1,
      border: "none",
      background: "transparent",
      outline: "none",
      fontSize: 13,
      fontFamily: "inherit",
      color: dark ? "#FAFAFA" : "#0F172A",
      "&::placeholder": { color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8" },
    },
    pickerList: { flex: 1, overflowY: "auto", padding: 6 },
    pickerOption: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 10px",
      borderRadius: 9,
      cursor: "pointer",
      transition: "background .12s",
      border: "none",
      background: "transparent",
      width: "100%",
      textAlign: "left",
      fontFamily: "inherit",
      "&:hover": { background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)" },
    },
    pickerOptionActive: {
      background: dark ? "rgba(91,124,255,0.14)" : "rgba(61,92,253,0.08)",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.20)" : "rgba(61,92,253,0.12)",
      },
    },
    pickerOptionText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    pickerOptionName: {
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    pickerOptionMeta: {
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    pickerCheck: { color: dark ? "#A5B4FC" : "#3D5CFD", flexShrink: 0 },
    pickerEmpty: {
      padding: "24px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
    },

    // ── SLA (popover compacto, estilo Jira/Linear) ──
    // Trigger: botão único do tamanho dos vizinhos (atribuído). Ao clicar
    // abre popover com presets + datetime + limpar.
    slaTrigger: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      width: "100%",
      height: 40,
      padding: "0 12px",
      borderRadius: 11,
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
      background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
      cursor: "pointer",
      transition: "border-color .15s, background .15s",
      fontFamily: "inherit",
      "&:hover": {
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.28)",
        background: dark ? "rgba(255,255,255,0.04)" : "#FFFFFF",
      },
    },
    slaTriggerActive: {
      // estado quando há SLA setado: borda colorida + bg sutil colorido
      // injetado via inline style (cor depende do status).
    },
    slaTriggerIcon: { flexShrink: 0 },
    slaTriggerText: {
      flex: 1,
      minWidth: 0,
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      textAlign: "left",
    },
    slaTriggerPlaceholder: { color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8", fontWeight: 400 },
    slaClearInlineBtn: {
      width: 22,
      height: 22,
      border: "none",
      borderRadius: 6,
      background: "transparent",
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      color: dark ? "rgba(255,255,255,0.5)" : "#94A3B8",
      transition: "background .12s, color .12s",
      flexShrink: 0,
      "&:hover": {
        background: "rgba(239,68,68,0.12)",
        color: "#ef4444",
      },
    },
    // Popover
    slaPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 14,
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        boxShadow: dark
          ? "0 20px 60px -12px rgba(0,0,0,0.6), 0 8px 24px -6px rgba(0,0,0,0.4)"
          : "0 20px 60px -12px rgba(15,23,42,0.18), 0 8px 24px -6px rgba(15,23,42,0.10)",
        width: 320,
        overflow: "hidden",
      },
    },
    slaPopoverHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "12px 14px 10px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    slaPopoverTitle: {
      flex: 1,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.65)" : "#475569",
    },
    slaPopoverBody: {
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    slaPresetsRow: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 },
    slaPresetCard: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "9px 12px",
      borderRadius: 9,
      border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.08)",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.75)" : "#475569",
      fontSize: 12.5,
      fontWeight: 500,
      cursor: "pointer",
      transition: "background .12s, border-color .12s, color .12s",
      fontFamily: "inherit",
      textAlign: "left",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)",
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.28)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    slaPresetCardActive: {
      background: dark ? "rgba(91,124,255,0.16)" : "rgba(61,92,253,0.08)",
      borderColor: dark ? "rgba(91,124,255,0.45)" : "rgba(61,92,253,0.32)",
      color: dark ? "#A5B4FC" : "#3D5CFD",
    },
    slaCustomLabel: {
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8",
      marginBottom: 6,
    },
    slaCustomInput: {
      width: "100%",
      "& .MuiOutlinedInput-root": {
        height: 36,
        borderRadius: 9,
        background: dark ? "rgba(255,255,255,0.025)" : "#FFFFFF",
        fontSize: 13,
        color: dark ? "#FAFAFA" : "#0F172A",
      },
      "& fieldset": {
        borderColor: dark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.10)",
      },
      "& .MuiOutlinedInput-input": { padding: "8px 12px" },
      "& input[type=datetime-local]::-webkit-calendar-picker-indicator": {
        filter: dark ? "invert(0.7)" : "none",
        cursor: "pointer",
        opacity: 0.7,
      },
    },
    slaPopoverFooter: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "10px 14px",
      borderTop: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      background: dark ? "rgba(0,0,0,0.2)" : "rgba(15,23,42,0.02)",
    },
    slaPopoverStatus: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "5px 10px",
      borderRadius: 7,
      fontSize: 11.5,
      fontWeight: 600,
      border: "1px solid",
      whiteSpace: "nowrap",
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      "& span": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    slaPopoverClearBtn: {
      height: 28,
      padding: "0 12px",
      borderRadius: 7,
      border: dark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.65)" : "#71717A",
      fontSize: 11.5,
      fontWeight: 500,
      cursor: "pointer",
      transition: "color .12s, background .12s, border-color .12s",
      fontFamily: "inherit",
      whiteSpace: "nowrap",
      "&:hover": {
        color: "#ef4444",
        borderColor: "rgba(239,68,68,0.32)",
        background: "rgba(239,68,68,0.06)",
      },
    },

    // ── IA pill ──
    aiPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
      padding: "4px 10px",
      borderRadius: 999,
      border: "none",
      background: dark
        ? "linear-gradient(135deg, rgba(139,92,246,0.16) 0%, rgba(91,124,255,0.16) 100%)"
        : "linear-gradient(135deg, rgba(139,92,246,0.10) 0%, rgba(91,124,255,0.10) 100%)",
      color: dark ? "#C4B5FD" : "#7C3AED",
      fontSize: 11,
      fontWeight: 600,
      cursor: "pointer",
      transition: "transform .12s, opacity .15s",
      fontFamily: "inherit",
      "&:hover:not(:disabled)": { transform: "translateY(-1px)" },
      "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
    },

    // ── Anexos ──
    uploadZone: {
      borderRadius: 12,
      padding: "22px 16px",
      textAlign: "center",
      cursor: "pointer",
      transition: "border-color .15s, background .15s",
      background: dark ? "rgba(255,255,255,0.015)" : "rgba(15,23,42,0.015)",
      border: `1.5px dashed ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.05)" : "rgba(61,92,253,0.03)",
        borderColor: dark ? "rgba(91,124,255,0.40)" : "rgba(61,92,253,0.30)",
      },
    },
    uploadZoneDragging: {
      borderColor: dark ? "#5B7CFF" : "#3D5CFD",
      background: dark ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)",
    },
    uploadIcon: {
      width: 28, height: 28,
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      marginBottom: 6,
    },
    uploadText: {
      fontSize: 13.5,
      fontWeight: 500,
      color: dark ? "rgba(255,255,255,0.62)" : "#475569",
    },
    uploadHint: {
      fontSize: 11.5,
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      marginTop: 3,
    },
    fileList: { display: "flex", flexDirection: "column", gap: 6, marginBottom: 10 },
    fileItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 12px",
      borderRadius: 10,
      background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)",
      border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
      transition: "background .12s, border-color .12s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)" },
    },
    fileItemCover: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 12px",
      borderRadius: 10,
      background: dark ? "rgba(139,92,246,0.08)" : "rgba(139,92,246,0.05)",
      border: `1px solid ${dark ? "rgba(139,92,246,0.28)" : "rgba(139,92,246,0.20)"}`,
    },
    fileThumb: { width: 32, height: 32, borderRadius: 6, objectFit: "cover", flexShrink: 0 },
    fileIconBox: {
      width: 32, height: 32, borderRadius: 6, flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
    },
    fileMeta: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 },
    fileName: {
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#FAFAFA" : "#0F172A",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    fileSize: {
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.4)" : "#94A3B8",
      fontVariantNumeric: "tabular-nums",
    },
    fileActionBtn: {
      width: 28, height: 28,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      border: "none", background: "transparent", borderRadius: 7,
      color: dark ? "rgba(255,255,255,0.4)" : "#94A3B8",
      cursor: "pointer",
      transition: "background .12s, color .12s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)" },
    },
    fileCoverActive: { color: "#8b5cf6" },
    fileDangerHover: { "&:hover": { color: "#ef4444", background: "rgba(239,68,68,0.10)" } },

    // ── Custom fields ──
    fieldRow: { display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 },
    fieldTypeChip: {
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      padding: "3px 7px",
      borderRadius: 6,
      whiteSpace: "nowrap",
      marginTop: 10,
      flexShrink: 0,
    },
    chipText: {
      background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.05)",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
    },
    chipWhatsapp: { background: "rgba(37,211,102,0.14)", color: "#25d366" },
    chipCpf: { background: "rgba(91,124,255,0.14)", color: "#5B7CFF" },
    chipEmail: { background: "rgba(245,158,11,0.14)", color: "#f59e0b" },
    chipCurrency: { background: "rgba(34,197,94,0.14)", color: "#22c55e" },
    showOnCardDot: {
      width: 6, height: 6, borderRadius: "50%",
      backgroundColor: "#22c55e",
      flexShrink: 0,
      marginTop: 16,
      boxShadow: "0 0 0 2px rgba(34,197,94,0.25)",
    },
    fieldMenu: {
      padding: 6,
      borderRadius: 7,
      color: dark ? "rgba(255,255,255,0.40)" : "#94A3B8",
      marginTop: 6,
      transition: "background .12s, color .12s",
      "&:hover": {
        color: dark ? "#FAFAFA" : "#0F172A",
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
      },
    },
    addFieldBtn: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      width: "100%",
      padding: "10px 14px",
      borderRadius: 10,
      border: `1.5px dashed ${dark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      cursor: "pointer",
      transition: "background .12s, border-color .12s, color .12s",
      fontSize: 12.5,
      fontWeight: 500,
      fontFamily: "inherit",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.06)" : "rgba(61,92,253,0.04)",
        borderColor: dark ? "rgba(91,124,255,0.32)" : "rgba(61,92,253,0.30)",
        color: dark ? "#A5B4FC" : "#3D5CFD",
      },
    },
    labelEditor: {
      "& .MuiInputBase-input": { fontSize: 13, fontWeight: 500, padding: "10px 14px" },
      "& .MuiOutlinedInput-root": { borderRadius: 10 },
      width: "100%",
    },

    // ── Popover "Adicionar campo" — layout premium custom ──
    addFieldPopover: {
      "& .MuiPopover-paper": {
        borderRadius: 14,
        background: dark ? "#1C1F26" : "#FFFFFF",
        border: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.08)",
        boxShadow: dark
          ? "0 20px 60px -12px rgba(0,0,0,0.6), 0 8px 24px -6px rgba(0,0,0,0.4)"
          : "0 20px 60px -12px rgba(15,23,42,0.18), 0 8px 24px -6px rgba(15,23,42,0.10)",
        width: 320,
        overflow: "hidden",
      },
    },
    addFieldHeader: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "12px 14px 10px",
      borderBottom: dark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
    },
    addFieldTitle: {
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.65)" : "#475569",
    },
    addFieldSection: { padding: "8px 8px" },
    addFieldSectionLabel: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: dark ? "rgba(255,255,255,0.42)" : "#94A3B8",
      padding: "4px 10px 6px",
    },
    addFieldDivider: {
      height: 1,
      background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.05)",
      margin: "0 14px",
    },
    addFieldPresetRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 12px",
      borderRadius: 9,
      border: "none",
      background: "transparent",
      width: "100%",
      cursor: "pointer",
      transition: "background .12s",
      fontFamily: "inherit",
      textAlign: "left",
      "&:hover": {
        background: dark ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)",
      },
    },
    addFieldPresetIcon: {
      width: 32,
      height: 32,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    addFieldRowText: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 },
    addFieldRowTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: dark ? "#FAFAFA" : "#0F172A",
    },
    addFieldRowDesc: {
      fontSize: 11,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    addFieldTypeGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 4,
      padding: "0 4px",
    },
    addFieldTypeRow: {
      display: "flex",
      alignItems: "center",
      gap: 9,
      padding: "8px 10px",
      borderRadius: 8,
      border: "none",
      background: "transparent",
      width: "100%",
      cursor: "pointer",
      transition: "background .12s, color .12s",
      fontFamily: "inherit",
      textAlign: "left",
      color: dark ? "rgba(255,255,255,0.78)" : "#334155",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
    },
    addFieldTypeIcon: { flexShrink: 0, display: "flex" },
    addFieldTypeLabel: { fontSize: 12.5, fontWeight: 500 },

    // ── Checklist ──
    checklistProgress: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
    checklistBar: {
      flex: 1, height: 5, borderRadius: 3,
      backgroundColor: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.06)",
      overflow: "hidden",
    },
    checklistBarFill: { height: "100%", borderRadius: 3, transition: "width .3s, background-color .3s" },
    checklistPercent: {
      fontSize: 11, fontWeight: 600,
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      minWidth: 36, textAlign: "right", fontVariantNumeric: "tabular-nums",
    },
    checklistItem: {
      display: "flex", alignItems: "center", gap: 4,
      marginBottom: 2, borderRadius: 7, padding: "2px 4px 2px 0",
      transition: "background .12s",
      "&:hover": { background: dark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.02)" },
      "&:hover $checklistDeleteBtn": { opacity: 1 },
    },
    checklistCheckbox: {
      padding: 6,
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      "&.Mui-checked": { color: "#22c55e" },
    },
    checklistText: { flex: 1, fontSize: 13, color: dark ? "rgba(255,255,255,0.85)" : "#334155" },
    checklistTextDone: {
      flex: 1, fontSize: 13,
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      textDecoration: "line-through",
    },
    checklistDeleteBtn: {
      padding: 4, opacity: 0, transition: "opacity .12s, color .12s",
      color: dark ? "rgba(255,255,255,0.32)" : "#94A3B8",
      "&:hover": { color: "#ef4444" },
      "@media (hover: none)": { opacity: 1, padding: 6 },
    },
    checklistAddRow: { display: "flex", alignItems: "center", gap: 6, marginTop: 6 },
    checklistInput: {
      "& .MuiInputBase-input": { fontSize: 13, padding: "8px 12px" },
      "& .MuiOutlinedInput-root": { borderRadius: 8 },
    },
    inlineBtn: {
      padding: "6px 12px", borderRadius: 8, border: "none",
      fontSize: 12, fontWeight: 600, cursor: "pointer",
      transition: "background .12s", fontFamily: "inherit",
    },
    inlineBtnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#FFFFFF",
      "&:hover:not(:disabled)": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    inlineBtnGhost: {
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.55)" : "#71717A",
      "&:hover": { background: dark ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)" },
    },
    checklistAddBtn: {
      display: "flex", alignItems: "center", gap: 5,
      padding: "6px 10px", borderRadius: 7, border: "none", background: "transparent",
      color: dark ? "rgba(255,255,255,0.45)" : "#71717A",
      cursor: "pointer", fontSize: 12, fontWeight: 500,
      transition: "background .12s, color .12s", fontFamily: "inherit",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)",
        color: dark ? "#A5B4FC" : "#3D5CFD",
      },
    },

    // ── Footer sticky ──
    footer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      padding: "14px 22px",
      borderTop: dark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(15,23,42,0.06)",
      background: dark ? "rgba(0,0,0,0.22)" : "rgba(15,23,42,0.02)",
      flexShrink: 0,
    },
    footerHint: {
      fontSize: 12,
      color: dark ? "rgba(255,255,255,0.45)" : "#94A3B8",
      display: "flex",
      alignItems: "center",
      gap: 7,
      minWidth: 0,
      overflow: "hidden",
    },
    footerHintIcon: { flexShrink: 0 },
    footerHintText: {
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    },
    footerActions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
    cancelBtn: {
      height: 38,
      padding: "0 18px",
      borderRadius: 10,
      border: dark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.75)" : "#475569",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      transition: "background .12s, color .12s",
      fontFamily: "inherit",
      "&:hover": {
        background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
        color: dark ? "#FAFAFA" : "#0F172A",
      },
      "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
    },
    submitBtn: {
      height: 38,
      padding: "0 20px",
      borderRadius: 10,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#FFFFFF",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      transition: "transform .12s, opacity .15s, box-shadow .15s",
      fontFamily: "inherit",
      position: "relative",
      boxShadow: "0 4px 14px -2px rgba(61,92,253,0.36)",
      "&:hover:not(:disabled)": {
        transform: "translateY(-1px)",
        boxShadow: "0 8px 22px -4px rgba(61,92,253,0.48)",
      },
      "&:active": { transform: "translateY(0)" },
      "&:disabled": { opacity: 0.6, cursor: "not-allowed", boxShadow: "none" },
    },
    submitBtnSpinner: {
      color: "#FFFFFF",
      position: "absolute",
      top: "50%",
      left: "50%",
      marginTop: -10,
      marginLeft: -10,
    },
  };
});

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────
// Cada tipo carrega ícone lucide + cor sutil (para o ícone no menu) e classe
// do chip que aparece à esquerda do campo no card.
const FIELD_TYPES = {
  text: { label: "Texto", icon: TypeIcon, iconColor: "#94A3B8", chipClass: "chipText" },
  whatsapp: { label: "WhatsApp", icon: WhatsappIconLc, iconColor: "#25d366", chipClass: "chipWhatsapp" },
  cpf: { label: "CPF", icon: CpfIconLc, iconColor: "#5B7CFF", chipClass: "chipCpf" },
  email: { label: "Email", icon: EmailIconLc, iconColor: "#f59e0b", chipClass: "chipEmail" },
  currency: { label: "Moeda", icon: CurrencyIconLc, iconColor: "#22c55e", chipClass: "chipCurrency" },
  checklist: { label: "Subtarefas", icon: ChecklistIconLc, iconColor: "#A5B4FC", chipClass: "chipText" },
};

const PRESETS = {
  leads: {
    label: "Leads",
    description: "Nome · CPF · WhatsApp · Email · Endereço · Valor",
    icon: UsersIconLc,
    accent: "#5B7CFF",
    fields: [
      { type: "text", label: "Nome" },
      { type: "cpf", label: "CPF" },
      { type: "whatsapp", label: "WhatsApp" },
      { type: "email", label: "Email" },
      { type: "text", label: "Endereço" },
      { type: "currency", label: "Valor" },
    ],
  },
};

const PRIORITIES = [
  { value: "low", label: "Baixa", color: "#22c55e", hint: "Sem urgência, pode aguardar" },
  { value: "normal", label: "Média", color: "#3b82f6", hint: "Fluxo normal de trabalho" },
  { value: "high", label: "Alta", color: "#f59e0b", hint: "Precisa de atenção" },
  { value: "urgent", label: "Maior", color: "#dc2626", hint: "Crítico, atendimento imediato" },
];

const SLA_PRESETS = [
  { key: "today", label: "Hoje", offsetDays: 0, hour: 18 },
  { key: "tomorrow", label: "Amanhã", offsetDays: 1, hour: 18 },
  { key: "two", label: "+2 dias", offsetDays: 2, hour: 18 },
  { key: "seven", label: "+7 dias", offsetDays: 7, hour: 18 },
];

// Quill — toolbar enxuta, focada nas formatações úteis pra um card.
const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    ["link", "clean"],
  ],
  clipboard: { matchVisual: false },
};
const QUILL_FORMATS = [
  "bold", "italic", "underline",
  "list", "bullet",
  "blockquote", "code-block",
  "link",
];

let fieldIdCounter = 0;
const generateFieldId = () => {
  fieldIdCounter += 1;
  return `f${Date.now()}_${fieldIdCounter}`;
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: UserAvatar (foto real ou fallback de iniciais coloridas)
// ─────────────────────────────────────────────────────────────────────────────
const UserAvatar = ({ user, size = 26, classes }) => {
  const [imgFailed, setImgFailed] = useState(false);

  // Reset do estado de falha quando muda o user (foto pode ter sido atualizada).
  useEffect(() => { setImgFailed(false); }, [user?.id, user?.profileImage]);

  const color = stringToColor(user?.name);
  const baseStyle = {
    width: size,
    height: size,
    fontSize: Math.max(9, Math.round(size * 0.42)),
  };

  // Backend salva profileImage como base64 Data URL inline. Aceita também
  // URL absoluta caso isso mude no futuro (não bloqueia, deixa o <img>
  // tentar e cai pro fallback se falhar).
  const hasPhoto = !imgFailed && !!user?.profileImage;

  if (hasPhoto) {
    return (
      <div
        className={classes.assigneeAvatar}
        style={{ ...baseStyle, border: `1px solid ${color}40` }}
      >
        <img
          src={user.profileImage}
          alt={user.name || ""}
          className={classes.assigneeAvatarImg}
          onError={() => setImgFailed(true)}
          loading="lazy"
        />
      </div>
    );
  }

  return (
    <div
      className={classes.assigneeAvatar}
      style={{
        ...baseStyle,
        background: `${color}26`,
        color,
        border: `1px solid ${color}40`,
      }}
    >
      {initials(user?.name)}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: AssigneePicker (avatar + busca local)
// ─────────────────────────────────────────────────────────────────────────────
const AssigneePicker = ({ classes, users, value, onChange, loading }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [search, setSearch] = useState("");
  const inputRef = useRef(null);

  const open = Boolean(anchorEl);
  const selected = useMemo(
    () => users.find((u) => u.id === value) || null,
    [users, value]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name || "").toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q));
  }, [users, search]);

  useEffect(() => {
    if (open && inputRef.current) {
      // foco no input ao abrir
      setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
    }
    if (!open) setSearch("");
  }, [open]);

  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);

  const pick = (userId) => {
    onChange(userId);
    closePopover();
  };

  return (
    <>
      <button type="button" className={classes.assigneeTrigger} onClick={openPopover}
        aria-haspopup="listbox" aria-expanded={open}>
        {selected ? (
          <>
            <UserAvatar user={selected} size={26} classes={classes} />
            <span className={classes.assigneeName}>{selected.name}</span>
          </>
        ) : (
          <>
            <span className={classes.assigneeAvatarPlaceholder}>
              <UserRound size={14} strokeWidth={1.75} />
            </span>
            <span className={`${classes.assigneeName} ${classes.assigneeNamePlaceholder}`}>
              Ninguém
            </span>
          </>
        )}
        <ArrowRight size={14} strokeWidth={2} className={classes.assigneeChevron}
          style={{ transform: "rotate(90deg)" }} />
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        className={classes.pickerPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className={classes.pickerSearchWrap}>
          <Search size={14} className={classes.pickerSearchIcon} />
          <input
            ref={inputRef}
            className={classes.pickerSearchInput}
            placeholder="Buscar por nome ou email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape") closePopover(); }}
          />
        </div>
        <div className={classes.pickerList}>
          {/* "Ninguém" — sempre no topo */}
          <button
            type="button"
            className={`${classes.pickerOption} ${!value ? classes.pickerOptionActive : ""}`}
            onClick={() => pick("")}
          >
            <span className={classes.assigneeAvatarPlaceholder}>
              <UserRound size={14} strokeWidth={1.75} />
            </span>
            <div className={classes.pickerOptionText}>
              <span className={classes.pickerOptionName}>Ninguém</span>
              <span className={classes.pickerOptionMeta}>Sem responsável definido</span>
            </div>
            {!value && <Check size={15} className={classes.pickerCheck} />}
          </button>

          {loading && (
            <div className={classes.pickerEmpty}>
              <CircularProgress size={16} style={{ marginRight: 8 }} />
              Carregando usuários...
            </div>
          )}

          {!loading && filtered.length === 0 && search && (
            <div className={classes.pickerEmpty}>Nenhum usuário encontrado.</div>
          )}

          {!loading && filtered.map((u) => {
            const isActive = value === u.id;
            return (
              <button
                key={u.id}
                type="button"
                className={`${classes.pickerOption} ${isActive ? classes.pickerOptionActive : ""}`}
                onClick={() => pick(u.id)}
              >
                <UserAvatar user={u} size={28} classes={classes} />
                <div className={classes.pickerOptionText}>
                  <span className={classes.pickerOptionName}>{u.name}</span>
                  {(u.email || u.profile) && (
                    <span className={classes.pickerOptionMeta}>
                      {u.profile && (u.profile === "admin" ? "Admin" : u.profile === "superadmin" ? "Super-admin" : "Usuário")}
                      {u.profile && u.email && " · "}
                      {u.email || ""}
                    </span>
                  )}
                </div>
                {isActive && <Check size={15} className={classes.pickerCheck} />}
              </button>
            );
          })}
        </div>
      </Popover>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-componente: SlaField (presets + datetime + badge)
// ─────────────────────────────────────────────────────────────────────────────
// SlaPopoverButton — trigger compacto + popover com presets/datetime/clear.
// Inspirado em date picker do Linear/Jira: zero altura desperdiçada quando
// não tem prazo; quando tem, mostra resumo formatado direto no botão.
const SlaPopoverButton = ({ classes, value, onChange, status }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const StatusIcon = status?.icon;

  const open = Boolean(anchorEl);
  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);

  const matchPreset = (preset) => {
    if (!value) return false;
    return slaPreset(preset.offsetDays, preset.hour) === value;
  };

  // Apply preset and close (UX: presets são "set + done"). Datetime custom
  // permanece aberto pra ajustes finos.
  const applyPreset = (p) => {
    onChange(slaPreset(p.offsetDays, p.hour));
    closePopover();
  };

  return (
    <>
      <button
        type="button"
        className={classes.slaTrigger}
        onClick={openPopover}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={value && status ? {
          borderColor: `${status.color}55`,
          background: status.bg,
        } : undefined}
      >
        <Calendar size={14} strokeWidth={2}
          className={classes.slaTriggerIcon}
          style={{ color: status?.color || (anchorEl ? "#5B7CFF" : undefined) }}
        />
        {value ? (
          <>
            <span className={classes.slaTriggerText} style={{ color: status?.color }}>
              SLA: {formatSlaShort(value)}
            </span>
            <Tooltip title="Limpar prazo" arrow>
              <button
                type="button"
                className={classes.slaClearInlineBtn}
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                aria-label="Remover prazo"
              >
                <X size={12} strokeWidth={2.25} />
              </button>
            </Tooltip>
          </>
        ) : (
          <span className={`${classes.slaTriggerText} ${classes.slaTriggerPlaceholder}`}>
            Definir prazo
          </span>
        )}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        className={classes.slaPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <div className={classes.slaPopoverHeader}>
          <Calendar size={14} strokeWidth={2} style={{ color: "#5B7CFF" }} />
          <span className={classes.slaPopoverTitle}>Definir prazo</span>
        </div>

        <div className={classes.slaPopoverBody}>
          {/* Presets rápidos */}
          <div className={classes.slaPresetsRow}>
            {SLA_PRESETS.map((p) => {
              const active = matchPreset(p);
              return (
                <button
                  key={p.key}
                  type="button"
                  className={`${classes.slaPresetCard} ${active ? classes.slaPresetCardActive : ""}`}
                  onClick={() => applyPreset(p)}
                >
                  <Clock size={13} strokeWidth={2} />
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* Datetime custom */}
          <div>
            <div className={classes.slaCustomLabel}>Data e hora personalizada</div>
            <TextField
              type="datetime-local"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              variant="outlined" size="small"
              className={classes.slaCustomInput}
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </div>

        {(value || status) && (
          <div className={classes.slaPopoverFooter}>
            {status && StatusIcon && (
              <span
                className={classes.slaPopoverStatus}
                style={{
                  background: status.bg,
                  color: status.color,
                  borderColor: status.border,
                }}
              >
                <StatusIcon size={12} strokeWidth={2.5} />
                <span>{status.label}</span>
              </span>
            )}
            {value && (
              <button
                type="button"
                className={classes.slaPopoverClearBtn}
                onClick={() => onChange("")}
              >
                Limpar
              </button>
            )}
          </div>
        )}
      </Popover>
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────
const CreateLeadModal = ({ open, onClose, statusId, boardId }) => {
  const classes = useStyles();

  const [submitting, setSubmitting] = useState(false);
  // Validação tardia: só marca erro depois que o usuário REALMENTE tentou
  // criar (clicou em "Criar card" com título vazio). Estado inicial fica
  // 100% limpo, sem borda vermelha de cara.
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [title, setTitle] = useState("");
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [fields, setFields] = useState([]);
  const [priority, setPriority] = useState("normal");
  const [assignedUserId, setAssignedUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [files, setFiles] = useState([]);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [coverIndex, setCoverIndex] = useState(null);
  const [aiReformulateOpen, setAiReformulateOpen] = useState(false);
  // Reformular com IA: hoje, aberto a todos; com a regra nova, ai.assist.
  const { can } = usePermission();
  const canAiAssist = can("ai.assist", true);
  const [slaDatetime, setSlaDatetime] = useState("");

  const [checklistAddingFor, setChecklistAddingFor] = useState(null);
  const [checklistNewText, setChecklistNewText] = useState("");

  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [fieldMenuAnchor, setFieldMenuAnchor] = useState(null);
  const [activeFieldId, setActiveFieldId] = useState(null);
  const [editingLabelFieldId, setEditingLabelFieldId] = useState(null);

  const slaStatus = useMemo(() => computeSlaStatus(slaDatetime), [slaDatetime]);
  const titleError = submitAttempted && !title.trim();

  // ── Effects ──
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingUsers(true);
    api.get("/users/list")
      .then(({ data }) => { if (!cancelled) setUsers(data || []); })
      .catch(() => { /* silent — picker mostra estado vazio */ })
      .finally(() => { if (!cancelled) setLoadingUsers(false); });
    return () => { cancelled = true; };
  }, [open]);

  // Ctrl+V paste handler for images (anexos)
  useEffect(() => {
    if (!open) return;
    const handlePaste = (e) => {
      // Quill captura paste de texto — só interceptamos se vier imagem.
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles = [];
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length === 0) return;
      e.preventDefault();
      const MAX = 50 * 1024 * 1024;
      const validFiles = [];
      for (const file of imageFiles) {
        if (file.size > MAX) {
          toast.error(`${file.name} excede o limite de 50MB`);
          continue;
        }
        validFiles.push({ file, preview: URL.createObjectURL(file) });
      }
      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
        toast.success(`${validFiles.length} imagem(ns) colada(s)`);
      }
    };
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [open]);

  const handleClose = () => {
    files.forEach((f) => { if (f.preview) URL.revokeObjectURL(f.preview); });
    setTitle("");
    setDescriptionHtml("");
    setFields([]);
    setFiles([]);
    setCoverIndex(null);
    setPriority("normal");
    setAssignedUserId("");
    setSlaDatetime("");
    setSubmitAttempted(false);
    setAddMenuAnchor(null);
    setFieldMenuAnchor(null);
    setActiveFieldId(null);
    setEditingLabelFieldId(null);
    setChecklistAddingFor(null);
    setChecklistNewText("");
    onClose();
  };

  // ── Field management ──
  const addField = (type, label) => {
    const field = { id: generateFieldId(), type, label, value: "", showOnCard: false };
    if (type === "checklist") { field.items = []; field.showOnCard = true; }
    setFields((prev) => [...prev, field]);
  };
  const addPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    if (!preset) return;
    const newFields = preset.fields.map((f) => ({
      id: generateFieldId(), type: f.type, label: f.label, value: "", showOnCard: false,
    }));
    setFields((prev) => [...prev, ...newFields]);
    setAddMenuAnchor(null);
  };
  const addSingleField = (type) => {
    const typeInfo = FIELD_TYPES[type];
    addField(type, typeInfo?.label || "Campo");
    setAddMenuAnchor(null);
  };
  const updateField = (fieldId, key, value) =>
    setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, [key]: value } : f)));
  const removeField = (fieldId) => {
    setFields((prev) => prev.filter((f) => f.id !== fieldId));
    setFieldMenuAnchor(null); setActiveFieldId(null);
  };
  const toggleShowOnCard = (fieldId) =>
    setFields((prev) => prev.map((f) => (f.id === fieldId ? { ...f, showOnCard: !f.showOnCard } : f)));

  // ── File management ──
  const MAX_FILE_SIZE = 50 * 1024 * 1024;
  const addFiles = (newFiles) => {
    const validFiles = [];
    for (const file of newFiles) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} excede o limite de 50MB`); continue;
      }
      const isImage = file.type.startsWith("image/");
      validFiles.push({ file, preview: isImage ? URL.createObjectURL(file) : null });
    }
    setFiles((prev) => [...prev, ...validFiles]);
  };
  const removeFile = (index) => {
    setFiles((prev) => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
    setCoverIndex((prev) => {
      if (prev === null) return null;
      if (prev === index) return null;
      if (prev > index) return prev - 1;
      return prev;
    });
  };
  const handleFileDrop = (e) => {
    e.preventDefault(); setIsDraggingFile(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) addFiles(droppedFiles);
  };
  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    if (selected.length > 0) addFiles(selected);
    e.target.value = "";
  };
  const getFileIcon = (file) => {
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (file.type.startsWith("image/")) return <ImageIcon size={16} style={{ color: "#8b5cf6" }} />;
    if (ext === "pdf") return <FileText size={16} style={{ color: "#ef4444" }} />;
    if (["doc", "docx", "xls", "xlsx", "ppt", "pptx", "txt"].includes(ext))
      return <FileText size={16} style={{ color: "#3b82f6" }} />;
    return <FileIcon size={16} style={{ color: "#94a3b8" }} />;
  };
  const uploadFilesToServer = async () => {
    if (files.length === 0) return [];
    const formData = new FormData();
    files.forEach((f) => formData.append("files", f.file));
    const { data } = await api.post("/external-leads/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  // ── Checklist ──
  const addChecklistItem = (fieldId, text) => {
    setFields((prev) => prev.map((f) => {
      if (f.id !== fieldId) return f;
      const item = {
        id: `c${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        text, done: false,
      };
      return { ...f, items: [...(f.items || []), item] };
    }));
  };
  const toggleChecklistItem = (fieldId, itemId) => {
    setFields((prev) => prev.map((f) => {
      if (f.id !== fieldId) return f;
      return { ...f, items: (f.items || []).map((c) => c.id === itemId ? { ...c, done: !c.done } : c) };
    }));
  };
  const deleteChecklistItem = (fieldId, itemId) => {
    setFields((prev) => prev.map((f) => {
      if (f.id !== fieldId) return f;
      return { ...f, items: (f.items || []).filter((c) => c.id !== itemId) };
    }));
  };
  const renameChecklistItem = (fieldId, itemId, text) => {
    setFields((prev) => prev.map((f) => {
      if (f.id !== fieldId) return f;
      return { ...f, items: (f.items || []).map((c) => c.id === itemId ? { ...c, text } : c) };
    }));
  };

  // ── Submit ──
  const handleSubmit = async () => {
    setSubmitAttempted(true);
    if (!title.trim()) {
      toast.error("Título é obrigatório");
      return;
    }
    setSubmitting(true);
    try {
      const attachments = await uploadFilesToServer();
      let coverUrl;
      if (coverIndex !== null && files[coverIndex] && attachments.length > 0) {
        const coverAttachment = attachments[coverIndex];
        if (coverAttachment) coverUrl = coverAttachment.url;
      }

      const whatsappField = fields.find((f) => f.type === "whatsapp" && f.value);
      const emailField = fields.find((f) => f.type === "email" && f.value);
      const cpfField = fields.find((f) => f.type === "cpf" && f.value);
      const addressField = fields.find((f) => f.label.toLowerCase() === "endereço" && f.value);

      const descriptionToSave = quillIsEmpty(descriptionHtml) ? undefined : descriptionHtml;

      const payload = {
        name: title.trim(),
        phone: whatsappField?.value || "",
        email: emailField?.value || "",
        cpf: cpfField?.value || "",
        address: addressField?.value || "",
        statusId, boardId,
        source: "manual",
        priority,
        assignedUserId: assignedUserId || undefined,
        attachments,
        customFields: {
          description: descriptionToSave,
          coverUrl: coverUrl || undefined,
          slaDate: slaDatetime || undefined,
          fields: fields.map((f) => {
            const base = { id: f.id, type: f.type, label: f.label, value: f.value, showOnCard: f.showOnCard };
            if (f.type === "checklist") base.items = f.items || [];
            return base;
          }),
        },
      };

      await api.post("/external-leads", payload);
      toast.success("Card criado com sucesso!");
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Field renderers ──
  const renderFieldInput = (field) => {
    const commonProps = {
      variant: "outlined", size: "small", fullWidth: true,
      className: classes.fieldInput, label: field.label, value: field.value,
    };
    switch (field.type) {
      case "whatsapp":
        return (
          <InputMask mask="(99) 99999-9999" value={field.value}
            onChange={(e) => updateField(field.id, "value", e.target.value)}>
            {(inputProps) => <TextField {...inputProps} {...commonProps} onChange={undefined} />}
          </InputMask>
        );
      case "cpf":
        return (
          <InputMask mask="999.999.999-99" value={field.value}
            onChange={(e) => updateField(field.id, "value", e.target.value)}>
            {(inputProps) => <TextField {...inputProps} {...commonProps} onChange={undefined} />}
          </InputMask>
        );
      case "currency":
        return (
          <NumberFormat customInput={TextField} variant="outlined" size="small" fullWidth
            className={classes.fieldInput} label={field.label}
            value={field.value === "" ? "" : Number(field.value)}
            thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2}
            allowNegative={false} isNumericString={false}
            onValueChange={(values) =>
              updateField(field.id, "value", values.floatValue !== undefined ? String(values.floatValue) : "")
            } />
        );
      case "email":
        return <TextField {...commonProps} type="email"
          onChange={(e) => updateField(field.id, "value", e.target.value)} />;
      case "checklist":
        return renderChecklistField(field);
      default:
        return <TextField {...commonProps}
          onChange={(e) => updateField(field.id, "value", e.target.value)} />;
    }
  };

  const renderChecklistField = (field) => {
    const items = field.items || [];
    const total = items.length;
    const done = items.filter((c) => c.done).length;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
      <div style={{ width: "100%" }}>
        {total > 0 && (
          <div className={classes.checklistProgress}>
            <div className={classes.checklistBar}>
              <div className={classes.checklistBarFill}
                style={{ width: `${pct}%`, backgroundColor: pct === 100 ? "#22c55e" : "#5B7CFF" }} />
            </div>
            <Typography className={classes.checklistPercent}>{done}/{total}</Typography>
          </div>
        )}
        {items.map((item) => (
          <ChecklistItemRow
            key={item.id}
            classes={classes}
            item={item}
            onToggle={() => toggleChecklistItem(field.id, item.id)}
            onDelete={() => deleteChecklistItem(field.id, item.id)}
            onRename={(text) => renameChecklistItem(field.id, item.id, text)}
          />
        ))}
        {checklistAddingFor === field.id ? (
          <div className={classes.checklistAddRow}>
            <TextField className={classes.checklistInput} variant="outlined" size="small" fullWidth
              placeholder="Nome da subtarefa..." value={checklistNewText}
              onChange={(e) => setChecklistNewText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  if (checklistNewText.trim()) {
                    addChecklistItem(field.id, checklistNewText.trim());
                    setChecklistNewText("");
                  }
                }
                if (e.key === "Escape") {
                  // Só desiste do item: o Esc subia, fechava o modal e zerava o card inteiro.
                  e.stopPropagation();
                  setChecklistAddingFor(null); setChecklistNewText("");
                }
              }} autoFocus />
            <button type="button" className={`${classes.inlineBtn} ${classes.inlineBtnPrimary}`}
              onClick={() => {
                if (checklistNewText.trim()) {
                  addChecklistItem(field.id, checklistNewText.trim());
                  setChecklistNewText("");
                }
              }} disabled={!checklistNewText.trim()}>
              Criar
            </button>
            <button type="button" className={`${classes.inlineBtn} ${classes.inlineBtnGhost}`}
              onClick={() => { setChecklistAddingFor(null); setChecklistNewText(""); }}>
              Cancelar
            </button>
          </div>
        ) : (
          <button className={classes.checklistAddBtn} type="button"
            onClick={() => { setChecklistAddingFor(field.id); setChecklistNewText(""); }}>
            <Plus size={14} /> Adicionar item
          </button>
        )}
      </div>
    );
  };

  const getChipClass = (type) => {
    const info = FIELD_TYPES[type];
    return info ? classes[info.chipClass] : classes.chipText;
  };

  // ── Render ──
  const FooterIcon = slaStatus?.icon || Calendar;
  const footerColor = slaStatus?.color;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth className={classes.dialog}>
      {/* Header */}
      <header className={classes.header}>
        <span className={classes.headerCrumb}>
          <span className={classes.headerCrumbDot} aria-hidden="true" />
          Novo card
        </span>
        <button type="button" className={classes.closeBtn} onClick={handleClose} aria-label="Fechar (Esc)">
          <X size={18} strokeWidth={1.75} />
        </button>
      </header>

      {/* Body */}
      <div className={classes.body}>
        {/* Título HERO — protagonista visual do modal */}
        <div className={classes.section} style={{ marginBottom: 14 }}>
          <TextField
            placeholder="Dê um título para este card..."
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              // Enter no título não submete (evita submit acidental antes
              // de preencher demais coisas) — só Tab/Click move adiante.
              if (e.key === "Enter") e.preventDefault();
            }}
            variant="outlined" fullWidth autoFocus multiline maxRows={3}
            className={`${classes.titleHero} ${titleError ? classes.titleHeroError : ""}`}
          />
          {titleError && (
            <span className={classes.fieldError} style={{ marginLeft: 24 }}>
              <AlertCircle size={12} strokeWidth={2} />
              Adicione um título para criar o card.
            </span>
          )}
        </div>

        {/* Descrição — rich text */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionLabel}>Descrição</span>
            {canAiAssist && (
              <button type="button" className={classes.aiPill}
                onClick={() => setAiReformulateOpen(true)}
                disabled={quillIsEmpty(descriptionHtml)}
                title="Reformular com IA">
                <Sparkles size={11} strokeWidth={2.25} />
                Reformular com IA
              </button>
            )}
          </div>
          <div className={classes.quillWrap}>
            <ReactQuill
              theme="snow"
              value={descriptionHtml}
              onChange={setDescriptionHtml}
              modules={QUILL_MODULES}
              formats={QUILL_FORMATS}
              placeholder="Adicione contexto, próximos passos, decisões importantes..."
            />
          </div>
        </div>

        {/* Detalhes — Prioridade, Atribuído, Prazo/SLA */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionLabel}>Detalhes</span>
          </div>
          <div className={classes.detailsGrid}>
            {/* Prioridade */}
            <div className={classes.detailCell}>
              <span className={classes.detailCellLabel}>Prioridade</span>
              <div className={classes.prioritySegment}>
                {PRIORITIES.map((p) => {
                  const active = priority === p.value;
                  return (
                    <Tooltip key={p.value} title={p.hint} arrow placement="top">
                      <button
                        type="button"
                        className={`${classes.priorityPill} ${active ? classes.priorityPillActive : ""}`}
                        onClick={() => setPriority(p.value)}
                        aria-pressed={active}
                      >
                        <span className={classes.priorityDot} style={{ backgroundColor: p.color }} />
                        {p.label}
                      </button>
                    </Tooltip>
                  );
                })}
              </div>
            </div>

            {/* Atribuído */}
            <div className={classes.detailCell}>
              <span className={classes.detailCellLabel}>Atribuído</span>
              <AssigneePicker
                classes={classes}
                users={users}
                value={assignedUserId}
                onChange={setAssignedUserId}
                loading={loadingUsers}
              />
            </div>

            {/* Prazo (SLA) — popover compacto */}
            <div className={classes.detailCell}>
              <span className={classes.detailCellLabel}>Prazo (SLA)</span>
              <SlaPopoverButton
                classes={classes}
                value={slaDatetime}
                onChange={setSlaDatetime}
                status={slaStatus}
              />
            </div>
          </div>
        </div>

        {/* Anexos */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionLabel}>Anexos</span>
          </div>
          {files.length > 0 && (
            <div className={classes.fileList}>
              {files.map((f, index) => {
                const isImage = f.file.type.startsWith("image/");
                const isCover = coverIndex === index;
                return (
                  <div key={index} className={isCover ? classes.fileItemCover : classes.fileItem}>
                    {isImage ? (
                      <img src={f.preview} alt="" className={classes.fileThumb} />
                    ) : (
                      <div className={classes.fileIconBox}>{getFileIcon(f.file)}</div>
                    )}
                    <div className={classes.fileMeta}>
                      <span className={classes.fileName} title={f.file.name}>{f.file.name}</span>
                      <span className={classes.fileSize}>{formatBytes(f.file.size)}</span>
                    </div>
                    {isImage && (
                      <Tooltip title={isCover ? "Remover capa" : "Usar como capa"}>
                        <button type="button"
                          className={`${classes.fileActionBtn} ${isCover ? classes.fileCoverActive : ""}`}
                          onClick={() => setCoverIndex(isCover ? null : index)}>
                          <ImageIcon size={14} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Remover">
                      <button type="button"
                        className={`${classes.fileActionBtn} ${classes.fileDangerHover}`}
                        onClick={() => removeFile(index)}>
                        <X size={14} />
                      </button>
                    </Tooltip>
                  </div>
                );
              })}
            </div>
          )}
          <div
            className={`${classes.uploadZone} ${isDraggingFile ? classes.uploadZoneDragging : ""}`}
            onClick={() => document.getElementById("lead-file-input").click()}
            onDragOver={(e) => { e.preventDefault(); setIsDraggingFile(true); }}
            onDragLeave={() => setIsDraggingFile(false)}
            onDrop={handleFileDrop}
          >
            <UploadCloud className={classes.uploadIcon} />
            <div className={classes.uploadText}>Clique ou arraste arquivos aqui</div>
            <div className={classes.uploadHint}>Imagens, PDFs, documentos — máx. 50MB por arquivo</div>
            <input id="lead-file-input" type="file" multiple style={{ display: "none" }} onChange={handleFileSelect} />
          </div>
        </div>

        {/* Campos personalizados */}
        <div className={classes.section}>
          <div className={classes.sectionHeader}>
            <span className={classes.sectionLabel}>Campos personalizados</span>
          </div>
          {fields.length > 0 && fields.map((field) => (
            <div key={field.id} className={classes.fieldRow}>
              {field.showOnCard && <div className={classes.showOnCardDot} title="Aparece no card" />}
              <span className={`${classes.fieldTypeChip} ${getChipClass(field.type)}`}>
                {FIELD_TYPES[field.type]?.label || "Texto"}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                {editingLabelFieldId === field.id ? (
                  <TextField
                    className={classes.labelEditor}
                    variant="outlined" size="small"
                    value={field.label}
                    onChange={(e) => updateField(field.id, "label", e.target.value)}
                    onBlur={() => setEditingLabelFieldId(null)}
                    onKeyDown={(e) => { if (e.key === "Enter") setEditingLabelFieldId(null); }}
                    autoFocus placeholder="Nome do campo"
                  />
                ) : (
                  renderFieldInput(field)
                )}
              </div>
              <IconButton className={classes.fieldMenu} size="small"
                onClick={(e) => { setActiveFieldId(field.id); setFieldMenuAnchor(e.currentTarget); }}>
                <MoreVertical size={16} />
              </IconButton>
            </div>
          ))}
          <button className={classes.addFieldBtn} onClick={(e) => setAddMenuAnchor(e.currentTarget)} type="button">
            <Plus size={14} strokeWidth={2} /> Adicionar campo
          </button>
        </div>

        {/* Popover: adicionar campo — layout premium custom (não usa Menu) */}
        <Popover
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          className={classes.addFieldPopover}
        >
          {/* Header */}
          <div className={classes.addFieldHeader}>
            <Plus size={14} strokeWidth={2.25} style={{ color: "#5B7CFF" }} />
            <span className={classes.addFieldTitle}>Adicionar campo</span>
          </div>

          {/* Section: Presets */}
          <div className={classes.addFieldSection}>
            <div className={classes.addFieldSectionLabel}>Modelo pronto</div>
            {Object.entries(PRESETS).map(([key, preset]) => {
              const PresetIcon = preset.icon;
              return (
                <button
                  key={key}
                  type="button"
                  className={classes.addFieldPresetRow}
                  onClick={() => addPreset(key)}
                >
                  <span className={classes.addFieldPresetIcon}
                    style={{
                      background: `${preset.accent}1F`,
                      color: preset.accent,
                      border: `1px solid ${preset.accent}33`,
                    }}>
                    <PresetIcon size={15} strokeWidth={1.75} />
                  </span>
                  <div className={classes.addFieldRowText}>
                    <span className={classes.addFieldRowTitle}>{preset.label}</span>
                    <span className={classes.addFieldRowDesc}>{preset.description}</span>
                  </div>
                </button>
              );
            })}
          </div>

          <div className={classes.addFieldDivider} />

          {/* Section: Tipos individuais */}
          <div className={classes.addFieldSection}>
            <div className={classes.addFieldSectionLabel}>Tipos de campo</div>
            <div className={classes.addFieldTypeGrid}>
              {Object.entries(FIELD_TYPES).map(([type, info]) => {
                const TypeIc = info.icon;
                return (
                  <button
                    key={type}
                    type="button"
                    className={classes.addFieldTypeRow}
                    onClick={() => addSingleField(type)}
                  >
                    <span className={classes.addFieldTypeIcon}
                      style={{ color: info.iconColor }}>
                      <TypeIc size={14} strokeWidth={1.75} />
                    </span>
                    <span className={classes.addFieldTypeLabel}>{info.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </Popover>

        {/* Menu: opções do campo */}
        <Menu
          anchorEl={fieldMenuAnchor} open={Boolean(fieldMenuAnchor)}
          onClose={() => { setFieldMenuAnchor(null); setActiveFieldId(null); }}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          getContentAnchorEl={null}
        >
          <MenuItem onClick={() => {
            if (activeFieldId) toggleShowOnCard(activeFieldId);
            setFieldMenuAnchor(null); setActiveFieldId(null);
          }}>
            <ListItemIcon style={{ minWidth: 36 }}><Eye size={16} /></ListItemIcon>
            <ListItemText primary="Exibir no card"
              primaryTypographyProps={{ style: { fontSize: 13 } }} />
            <Checkbox
              checked={fields.find((f) => f.id === activeFieldId)?.showOnCard || false}
              size="small" color="primary"
              style={{ padding: 0, marginLeft: 8 }}
            />
          </MenuItem>
          {fields.find((f) => f.id === activeFieldId)?.type === "text" && (
            <MenuItem onClick={() => {
              if (activeFieldId) setEditingLabelFieldId(activeFieldId);
              setFieldMenuAnchor(null); setActiveFieldId(null);
            }}>
              <ListItemIcon style={{ minWidth: 36 }}><Pencil size={16} /></ListItemIcon>
              <ListItemText primary="Renomear campo"
                primaryTypographyProps={{ style: { fontSize: 13 } }} />
            </MenuItem>
          )}
          <MenuItem onClick={() => { if (activeFieldId) removeField(activeFieldId); }}>
            <ListItemIcon style={{ minWidth: 36 }}><Trash2 size={16} style={{ color: "#ef4444" }} /></ListItemIcon>
            <ListItemText primary="Remover campo"
              primaryTypographyProps={{ style: { fontSize: 13, color: "#ef4444" } }} />
          </MenuItem>
        </Menu>
      </div>

      {/* Footer sticky */}
      <footer className={classes.footer}>
        <div className={classes.footerHint}>
          <FooterIcon size={14} strokeWidth={2}
            style={{ color: footerColor || undefined }}
            className={classes.footerHintIcon} />
          <span className={classes.footerHintText}
            style={{ color: footerColor || undefined }}>
            {slaDatetime
              ? `SLA definido para ${formatSlaShort(slaDatetime)}`
              : "Sem prazo (SLA) definido"}
          </span>
        </div>
        <div className={classes.footerActions}>
          <button type="button" className={classes.cancelBtn}
            onClick={handleClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="button" className={classes.submitBtn}
            onClick={handleSubmit} disabled={submitting || !title.trim()}>
            {submitting ? (
              <>
                <span style={{ opacity: 0 }}>Criar card</span>
                <CircularProgress size={18} className={classes.submitBtnSpinner} />
              </>
            ) : (
              <>
                Criar card
                <ArrowRight size={14} strokeWidth={2.25} />
              </>
            )}
          </button>
        </div>
      </footer>

      <AiReformulateDescriptionModal
        open={aiReformulateOpen}
        onClose={() => setAiReformulateOpen(false)}
        title={title || ""}
        originalDescription={descriptionHtml}
        onApply={(newHtml) => {
          setDescriptionHtml(newHtml || "");
          setAiReformulateOpen(false);
        }}
      />
    </Dialog>
  );
};

export default CreateLeadModal;
